import { MetricStrip } from "@/components/operations/MetricStrip";
import { useAuth } from "@/hooks/useAuth";
import { useOperationsSummary } from "@/hooks/useOperationsSummary";

import {
  buildHomeOverviewModel,
  type HomeQueueFilter,
} from "./homeOverviewModel";

export type { HomeQueueFilter } from "./homeOverviewModel";

export interface HomeOverviewPanelProps {
  queueFilter: HomeQueueFilter;
  onQueueFilterChange: (filter: HomeQueueFilter) => void;
}

export default function HomeOverviewPanel({
  queueFilter,
  onQueueFilterChange,
}: HomeOverviewPanelProps) {
  const { hasRole } = useAuth();
  // 指標端點僅開放 admin/staff；driver 不查詢，指標退回 0（同其原本無待辦佇列的體驗）。
  const canViewMetrics = hasRole("admin", "staff");
  const { data: summary } = useOperationsSummary({ enabled: canViewMetrics });

  const { pendingCount, preparingCount, readyCount, summaryText } =
    buildHomeOverviewModel(summary);

  const toggleFilter = (status: Exclude<HomeQueueFilter, "all">) => {
    onQueueFilterChange(queueFilter === status ? "all" : status);
  };

  return (
    <section id="homeOverviewPanel" className="space-y-4">
      <h2 className="text-xl font-bold tracking-tight text-ink-strong">{summaryText}</h2>

      {/* 漏斗三階段互斥、加總=摘要句總數；彩色只標「可出貨」的可行動狀態。 */}
      <MetricStrip
        className="sm:grid-cols-3 xl:grid-cols-3"
        metrics={[
          {
            label: "待備貨",
            value: `${pendingCount} 筆`,
            helper: "尚未開始備貨 · 點擊只看此階段",
            onClick: () => toggleFilter("pending"),
            active: queueFilter === "pending",
          },
          {
            label: "備貨中",
            value: `${preparingCount} 筆`,
            helper: "產線進行中 · 點擊只看此階段",
            onClick: () => toggleFilter("preparing"),
            active: queueFilter === "preparing",
          },
          {
            label: "可出貨",
            value: `${readyCount} 筆`,
            helper: "備貨完成，可安排出貨",
            tone: readyCount > 0 ? "success" : "neutral",
            onClick: () => toggleFilter("ready_to_ship"),
            active: queueFilter === "ready_to_ship",
          },
        ]}
      />
    </section>
  );
}
